import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { listRunningSubagentParents } from '../services/subagentsApi';
import UserStreamManager from '../utils/UserStreamManager';
import { useAuth } from './AuthContext';

/**
 * SubagentRunningContext (Nitish, Oct 17 2026, after CEL-3502): which agents have subagents still running, so a sidebar row
 * (an AI employee or a chat) shows the pulsing Subagents icon while its own run is stopped and its children work. Mirrors
 * GET /subagents/running ({ parents: { [parent id]: count } }; parent id = a chat id or an employee's anchor id, the ids the
 * rows carry). Refetched on SUBAGENT_UPDATED (create, new take, wake, never reported, cancel), on reconnect, and every minute
 * while the tab is visible as a backstop. The ApprovalStampContext shape.
 */
const EMPTY = Object.freeze({});
const SubagentRunningContext = createContext(EMPTY);
const POLL_MS = 60000;
const DEBOUNCE_MS = 500;

export const SubagentRunningProvider = ({ children }) => {
  const { currentUser, userProfile } = useAuth();
  const [parents, setParents] = useState(EMPTY);
  const debounceRef = useRef(null);
  const joined = !!currentUser && userProfile?.onboarding_status === 'JOINED';

  const refresh = useCallback(async () => {
    try {
      const res = await listRunningSubagentParents();
      setParents(res.data?.parents || EMPTY);
    } catch (e) {
      // Keep the last known icons; the next event or poll corrects them.
    }
  }, []);

  const scheduleRefresh = useCallback(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(refresh, DEBOUNCE_MS);
  }, [refresh]);

  useEffect(() => {
    if (!joined) {
      setParents(EMPTY);
      return undefined;
    }
    refresh();
    const unsubs = [
      UserStreamManager.subscribe('SUBAGENT_UPDATED', scheduleRefresh),
      UserStreamManager.addReconciliationCallback(() => refresh()),
    ];
    const poll = setInterval(() => {
      if (typeof document !== 'undefined' && document.hidden) return;
      refresh();
    }, POLL_MS);
    return () => {
      unsubs.forEach((u) => typeof u === 'function' && u());
      clearInterval(poll);
      clearTimeout(debounceRef.current);
    };
  }, [joined, refresh, scheduleRefresh]);

  return <SubagentRunningContext.Provider value={parents}>{children}</SubagentRunningContext.Provider>;
};

/** { [parent id]: running count }; a row reads `useRunningSubagents()[id] || 0`. */
export const useRunningSubagents = () => useContext(SubagentRunningContext);

export default SubagentRunningContext;
